import {
	computeContextMenuTitle,
	ContextMenu,
	DateString,
	TableView,
	VirtualTableViewColumnDefinition,
	useContextMenu,
	TableViewNoResultConfig
} from "client/Components";
import { SortingFor } from "client/Data/sorting";
import {
	MultiSelectableOptions,
	SelectableItemOptions,
	useIsTouchDevice,
	useRowHover,
	useUserTimezone
} from "client/Hooks";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DocumentName, DocumentNameKind } from "../DocumentName";
import { WebDavDocumentAction, WebDavDocumentActionContext } from "./actions";
import { GridItem } from "./WebDavOverview";
import { DocumentNameWithQuickActions } from "../DocumentNameWithQuickActions";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";
import { TimeZone } from "client/DateAndTime/timezones";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type WebDavTableViewHandle = {
	resetScrollPosition: () => void;
};

export type WebDavTableViewProps = {
	isBusy: boolean;
	sortingFor: SortingFor<GridItem>;
	multiSelection: MultiSelectableOptions;
	selectionFor: (item: GridItem) => SelectableItemOptions;
	items: readonly GridItem[];
	onOpenItem: (item: FileSystemItemModel) => void;
	selectionActions: readonly WebDavDocumentAction[];
	actionContext: WebDavDocumentActionContext;
	hasSearch: boolean;
};

export const WebDavTableView = forwardRef(
	(props: WebDavTableViewProps, ref: Ref<WebDavTableViewHandle>): ReactElement => {
		const {
			isBusy,
			sortingFor,
			multiSelection,
			selectionFor,
			items,
			onOpenItem,
			selectionActions,
			actionContext,
			hasSearch
		} = props;

		const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);
		const isTouch: boolean = useIsTouchDevice();
		const [hoveredRowId, hoverHandler] = useRowHover<string>();
		const userTimezone: TimeZone = useUserTimezone();

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					tableRef.current
						?.scrollTo(0, 0);
				}
			}),
			[]
		);

		const columns: readonly VirtualTableViewColumnDefinition[] = useColumnDefinitions(
			sortingFor
		);

		const noResultConfig = useMemo(
			(): TableViewNoResultConfig | undefined => {
				if (isBusy) {
					return undefined;
				}

				return hasSearch
					? {
						icon: "magnifying-glass",
						title: localize("_NoSearchResults"),
						message: localize("_PleaseTrySearchingWithAnotherTerm")
					}
					: {
						icon: "folder-open",
						title: localize("_NoDocumentsToShow"),
						message: localize("_ThereAreCurrentlyNoDocumentsInThisFolder")
					};
			},
			[hasSearch, isBusy]
		);

		return (
			<>
				<TableView
					isVirtual
					hasNoBottomDelimiter
					columns={columns}
					items={items}
					height="fill"
					itemKey="id"
					trackingId="webdav-grid"
					multiSelectable={multiSelection}
					ref={tableRef}
					renderAfterItems={
						<>
							<TableView.LoadMoreIndicator
								isLoadingData={isBusy}
								hasMoreItems={false}
								onRequestMoreItems={() => { /* NOOP */}}
							/>

							{items.length > 0 && (
								<AiAssistantFabSpacing />
							)}
						</>
					}
					noResultConfig={noResultConfig}
				>
					{(x): ReactElement => {
						const selectable: SelectableItemOptions = selectionFor(x);
						const isSelected: boolean = selectable && selectable.isSelected;
						return (
							<TableView.Row
								trackingId={x.id}
								selectable={selectable}
								onContextMenu={contextMenuHandler}
								onIsHoverChange={(isHover: boolean) => hoverHandler(x.id, isHover)}
							>
								<TableView.Cell>
									<DocumentNameWithQuickActions
										selectionActions={selectionActions}
										actionContext={{...actionContext, selectedDocuments: [x.model]}}
										isTouch={isTouch}
										isSelected={isSelected}
										isRowHovered={hoveredRowId === x.id}
										name={x.name}
									>
										<DocumentName
											name={x.name}
											documentKind={x.kind}
											mode={{
												kind: DocumentNameKind.Default,
												hasBookmark: x.hasBookmark
											}}
											onClick={() => onOpenItem(x.model)}
										/>
									</DocumentNameWithQuickActions>
								</TableView.Cell>
								<TableView.Cell>
									{x.creationDate && (
										<DateString
											value={x.creationDate}
											format="RelativeTimestamp"
											timezone={userTimezone}
										/>
									)}
								</TableView.Cell>
								<TableView.Cell>
									{x.modifiedDate && (
										<DateString
											value={x.modifiedDate}
											format="RelativeTimestamp"
											timezone={userTimezone}
										/>
									)}
								</TableView.Cell>
								<TableView.Cell>{x.extension}</TableView.Cell>
								<TableView.Cell>
									{getDisplayFriendlySize(x.size)}
								</TableView.Cell>
							</TableView.Row>
						);
					}}
				</TableView>
				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={selectionActions}
						context={actionContext}
						trackingId="webdav-context-menu"
						title={computeContextMenuTitle(
							actionContext.selectedDocuments,
							(x) => x.name
						)}
					/>
				)}
			</>
		);
	}
);
WebDavTableView.displayName = "WebDavTableView";

function useColumnDefinitions(
	sortingFor: SortingFor<GridItem>
): readonly VirtualTableViewColumnDefinition[] {
	return useMemo(
		(): readonly VirtualTableViewColumnDefinition[] => {
			return [
				{
					title: localize("_Name"),
					sortable: sortingFor("nameSorting"),
					trackingId: "name",
					width: 400
				},
				{
					title: localize("_DateCreated"),
					sortable: sortingFor("creationDateSorting"),
					trackingId: "created-on",
					width: 150
				},
				{
					title: localize("_DateModified"),
					sortable: sortingFor("modifiedDateSorting"),
					trackingId: "modified-on",
					width: 150
				},
				{
					title: localize("_Extension"),
					sortable: sortingFor("extension"),
					trackingId: "extension",
					width: 150
				},
				{
					title: localize("_FileSize"),
					sortable: sortingFor("size"),
					trackingId: "size",
					width: 150
				}
			];
		},
		[sortingFor]
	);
}
